import signalConfig from '../constants/signalConfig';
import humanizeString from 'humanize-string';
import * as signalNames from './../constants/signalNames';
import * as signalTypes from './../constants/signalTypes';

export const getSignalName = (type) => {
  const config = signalConfig[type.toLowerCase()];
  if (config) {
    return config.title;
  }
  return humanizeString(type);
};

export const getSignalSubtitle = (flattenSignal) => {
  const config = signalConfig[flattenSignal.signalKey.toLowerCase()];

  if (!config) {
    return '';
  }

  const description =
    flattenSignal.description ||
    flattenSignal.details.description ||
    config.description ||
    (flattenSignal.pages ? flattenSignal.pages[0].description : '');

  return config.getDescription
    ? config.getDescription(flattenSignal)
    : humanizeString(description, { preserveCase: true });
};

/**
 * This function returns an unique string that identifies an instance of a Signal
 * This can be used to identify a specific instance (e.g. tooltips, hide on hover, etc...)
 * The way it works is that i creates a base64 string of the bounding boxes of the instance, which is the differentiator for each instance
 * it takes than the last 20 characters of the base 64 and removes any trailing =, plus it adds the x0 and top values to make sure it results with an unique ID
 */
export const getInstanceId = (
  instance,
  pageNumber,
  signalKey,
  boundingBoxId = 0,
) => {
  const baseObject = { bounding_boxes: instance.bounding_boxes };
  const instanceStringStringified = btoa(encodeURI(JSON.stringify(baseObject)));
  const instanceString = instanceStringStringified
    .substring(
      instanceStringStringified.length - 20,
      instanceStringStringified.length,
    )
    .replace(/=/g, '');
  return `instance-id-${signalKey}-${instanceString}-${pageNumber}-${
    instance.bounding_boxes?.length
      ? `${parseInt(instance?.bounding_boxes[boundingBoxId].x0)}-${parseInt(
          instance.bounding_boxes[boundingBoxId].top,
        )}`
      : ''
  }`;
};

export const getBaseRoute = (route, document) => {
  return {
    customer_id: route.params.customer_id || document.customer_id,
    document_id: route.params.document_id || document.id,
    query: route.query,
  };
};

/**
 * add instances data
 * @param {} pages
 */
export const extendPages = (
  pages,
  signalKey,
  disabledInstances,
  disabledGroups,
  route,
  document,
) => {
  pages.forEach((page) => {
    page.instances?.forEach((instance) => {
      instance.id = getInstanceId(instance, page.page_number, signalKey);
      instance.enabled =
        !disabledInstances.includes(instance.id) &&
        !disabledGroups.includes(signalKey);
      instance.link = {
        ...getBaseRoute(route, document),
        query: {
          ...route.query,
          documentPage: page.page_number,
          instance: instance.id,
        },
        name: 'document-signals',
      };
    });
  });
};

export const signalHasRegions = (fraud_result, signalKey) => {
  return (
    fraud_result.details.pages?.length > 0 &&
    ![signalNames.TEMPLATES, signalNames.BLOCKLISTS].includes(signalKey)
  );
};

export const signalHasDescription = (signalKey) => {
  return [signalNames.ADOBE_FONTS].includes(signalKey);
};

export const signalHasViolations = (signalKey) => {
  return [signalNames.W2_SOCIAL_SECURITY_LIMIT_EXCEEDED].includes(signalKey);
};

export const signalHasDetailsView = (signalKey) => {
  return (
    [
      signalNames.SEEN_BEFORE,
      signalNames.COPYCAT_TEXT,
      signalNames.BLOCKLISTS,
      signalNames.REJECTED_CUSTOMER,
      signalNames.TEMPLATES,
      signalNames.REPEAT_ACCOUNT_NUMBER,
      signalNames.HIGH_RISK_CUSTOMER_SEEN_BEFORE,
      signalNames.REPEAT_BALANCES,
      signalNames.SOFTWARE_BLOCKLIST,
      signalNames.DATE_METADATA,
      signalNames.ADOBE_FONTS,
      signalNames.TEXT_LAYER_TEXT,
      signalNames.W2_SOCIAL_SECURITY_LIMIT_EXCEEDED,
    ].includes(signalKey) || isTransactionSignal(signalKey)
  );
};

export const signalHasDatabasesTable = (signalKey) => {
  return [
    signalNames.BLOCKLISTS,
    signalNames.REJECTED_CUSTOMER,
    signalNames.TEMPLATES,
    signalNames.HIGH_RISK_CUSTOMER_SEEN_BEFORE,
    signalNames.SOFTWARE_BLOCKLIST,
    signalNames.DATE_METADATA,
    signalNames.TEXT_LAYER_TEXT,
  ].includes(signalKey);
};

export const isTransactionSignal = (signalKey) => {
  return [
    signalNames.BANK_CHARGE_IDENTIFIED,
    signalNames.CRYPTO_TRADING_IDENTIFIED,
    signalNames.GAMBLING_IDENTIFIED,
    signalNames.LOANS_IDENTIFIED,
    signalNames.UNUSUAL_ROUND_NUMBER_DISTRIBUTION,
  ].includes(signalKey);
};

export const signalHasPreviousDocumentsTable = (signalKey) => {
  return [
    signalNames.SEEN_BEFORE,
    signalNames.COPYCAT_TEXT,
    signalNames.REPEAT_ACCOUNT_NUMBER,
    signalNames.REPEAT_BALANCES,
  ].includes(signalKey);
};

export const getSignalLink = (signal, options) => {
  const { signalKey, type, page_number } = signal;

  let signalParts = {
    name: 'document-signals',
    query: { page_number },
  };

  if (
    signalKey === signalNames.XRAY ||
    signalKey === signalNames.XRAY_LIMITED
  ) {
    signalParts = { name: 'document-recovered' };
  }

  if (type === signalTypes.CUSTOM_RULE) {
    signalParts = { name: 'rules-settings-view' };
  }

  const { name, params, query } = signalParts;
  return {
    name,
    params: {
      customer_id: options.customer_id,
      document_id: options.document_id,
      ...params,
    },
    query: {
      ...options.query,
      ...query,
    },
  };
};
